import { Link as ReactRouterLink, useLocation } from 'react-router';
import { Link as WaspRouterLink, routes } from 'wasp/client/router';
import { useAuth } from 'wasp/client/auth';
import { useState, useRef, useEffect, Dispatch, SetStateAction } from 'react';
import { Dialog } from '@headlessui/react';
import { BiLogIn } from 'react-icons/bi';
import { AiFillCloseCircle } from 'react-icons/ai';
import { HiBars3 } from 'react-icons/hi2';
import { Button, OptimisticNewsLogo } from '../../../components/common';
import DropdownUser from '../../../user/DropdownUser';
import { UserMenuItems } from '../../../user/UserMenuItems';
import DarkModeSwitcher from '../DarkModeSwitcher';
import { useIsLandingPage } from '../../hooks/useIsLandingPage';
import { cn } from '../../cn';
import {
  buildPublicationFeedPath,
  buildPublicationFeedPathEn,
  buildPublicationFeedPathDe,
} from '../../../shared/publicationRoutes';

export interface NavigationItem {
  name: string;
  to: string;
  navigationKind?: 'spa' | 'document';
}

const NavLogo = () => (
  <OptimisticNewsLogo
    className="h-14 w-[10.75rem] object-contain object-left origin-left scale-110"
    alt="Optimistic News"
  />
);

const NAV_INTERACTION_TEXT_CLASS_NAME =
  'duration-300 ease-in-out text-neutral-900 hover:text-primary dark:text-white dark:hover:text-primary';
const MOBILE_MENU_TOGGLE_BUTTON_CLASS_NAME =
  '-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-neutral-700 dark:text-white';

function LoginActionLabel({
  className = '',
  iconClassName = 'ml-1 mt-[0.1rem]',
}: {
  className?: string;
  iconClassName?: string;
}) {
  return (
    <span
      className={cn(
        'flex items-center',
        NAV_INTERACTION_TEXT_CLASS_NAME,
        className,
      )}
    >
      Log in <BiLogIn size="1.1rem" className={iconClassName} />
    </span>
  );
}

const RSS_FEEDS = [
  { label: '🌐 All Languages', href: buildPublicationFeedPath() },
  { label: '🇬🇧 English', href: buildPublicationFeedPathEn() },
  { label: '🇩🇪 German', href: buildPublicationFeedPathDe() },
];

const RSS_BUTTON_CLASS =
  'group flex h-7.5 w-7.5 items-center justify-center rounded-full text-neutral-400 duration-300 ease-in-out hover:text-primary dark:text-neutral-500 dark:hover:text-primary';

const RSS_LINK_CLASS =
  'block w-full px-3 py-1.5 text-left text-sm text-neutral-700 duration-200 ease-in-out hover:bg-primary/10 hover:text-primary dark:text-neutral-200 dark:hover:bg-primary/10 dark:hover:text-primary';

function RssFeedDropdown() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLLIElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [open]);

  return (
    <li ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={RSS_BUTTON_CLASS}
        aria-label="RSS Feeds"
        title="RSS Feeds"
      >
        <svg
          className="h-5 w-5 fill-current"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="6.18" cy="17.82" r="2.18" />
          <path d="M4 4.44v2.83c7.03 0 12.73 5.7 12.73 12.73h2.83c0-8.59-6.97-15.56-15.56-15.56zm0 5.66v2.83c3.9 0 7.07 3.17 7.07 7.07h2.83c0-5.47-4.43-9.9-9.9-9.9z" />
        </svg>
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-40 rounded-md border border-neutral-200 bg-white py-1 shadow-lg dark:border-neutral-600 dark:bg-boxdark">
          <p className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-neutral-400 dark:text-neutral-500">
            RSS Feeds
          </p>
          {RSS_FEEDS.map((feed) => (
            <a
              key={feed.href}
              href={feed.href}
              target="_blank"
              rel="noopener noreferrer"
              className={RSS_LINK_CLASS}
              onClick={() => setOpen(false)}
            >
              {feed.label}
            </a>
          ))}
        </div>
      )}
    </li>
  );
}

export default function AppNavBar({
  navigationItems,
}: {
  navigationItems: NavigationItem[];
}) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isLandingPage = useIsLandingPage();
  const { pathname } = useLocation();

  const { data: user, isLoading: isUserLoading } = useAuth();
  return (
    <header
      className={cn('absolute inset-x-0 top-0 z-50', {
        'dark:bg-boxdark-2': isLandingPage,
        'shadow-sm sticky bg-white/50 backdrop-blur-lg backdrop-filter dark:bg-boxdark-2/70 dark:border dark:border-neutral-100/10':
          !isLandingPage,
      })}
    >
      <nav
        className="flex items-center justify-between px-6 py-2 lg:px-8"
        aria-label="Global"
      >
        <div className="-ml-2 flex items-center lg:flex-1">
          <WaspRouterLink
            to={routes.LandingPageRoute.to}
            className="flex items-center text-neutral-900 duration-300 ease-in-out hover:text-primary dark:text-white dark:hover:text-primary"
            aria-label="Optimistic News"
          >
            <NavLogo />
          </WaspRouterLink>
        </div>
        <div className="flex lg:hidden">
          <Button
            type="button"
            variant="ghost"
            tone="neutral"
            size="sm"
            className={cn(
              MOBILE_MENU_TOGGLE_BUTTON_CLASS_NAME,
              'hover:bg-transparent dark:hover:bg-transparent',
            )}
            onClick={() => setMobileMenuOpen(true)}
          >
            <span className="sr-only">Open main menu</span>
            <HiBars3 className="h-6 w-6" aria-hidden="true" />
          </Button>
        </div>
        <div className="hidden lg:flex lg:gap-x-6 xl:gap-x-12">
          {renderNavigationItems(navigationItems)}
        </div>
        <div className="hidden lg:flex lg:flex-1 justify-end items-center shrink-0">
          <ul className="flex items-center gap-3">
            <RssFeedDropdown />
            <li>
              <DarkModeSwitcher />
            </li>
          </ul>
          <div className="mx-3 h-6 w-px bg-neutral-200 dark:bg-neutral-700" />
          {isUserLoading ? null : !user ? (
            <WaspRouterLink
              to={routes.LoginRoute.to}
              className="text-sm font-semibold leading-6"
            >
              <LoginActionLabel />
            </WaspRouterLink>
          ) : (
            <DropdownUser user={user} />
          )}
        </div>
      </nav>
      <Dialog
        as="div"
        className="lg:hidden"
        open={mobileMenuOpen}
        onClose={setMobileMenuOpen}
      >
        <div className="fixed inset-0 z-50" />
        <Dialog.Panel className="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-white dark:text-white dark:bg-boxdark px-6 pt-2 pb-6 sm:max-w-sm sm:ring-1 sm:ring-neutral-900/10">
          <div className="flex items-center justify-between">
            <WaspRouterLink
              to={routes.LandingPageRoute.to}
              className="-ml-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="sr-only">Optimistic News</span>
              <NavLogo />
            </WaspRouterLink>
            <Button
              type="button"
              variant="ghost"
              tone="neutral"
              size="sm"
              className={cn(
                MOBILE_MENU_TOGGLE_BUTTON_CLASS_NAME,
                'dark:text-neutral-50 hover:bg-transparent dark:hover:bg-transparent',
              )}
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="sr-only">Close menu</span>
              <AiFillCloseCircle className="h-6 w-6" aria-hidden="true" />
            </Button>
          </div>
          <div className="mt-6 flex flex-col gap-6">
            <div className="space-y-2 text-center">
              {renderNavigationItems(
                navigationItems,
                setMobileMenuOpen,
                pathname,
              )}
            </div>
            <div className="border-t border-neutral-500/10 pt-6">
              {isUserLoading ? null : !user ? (
                <WaspRouterLink to={routes.LoginRoute.to}>
                  <LoginActionLabel
                    className="justify-center"
                    iconClassName="ml-1"
                  />
                </WaspRouterLink>
              ) : (
                <UserMenuItems
                  user={user}
                  setMobileMenuOpen={setMobileMenuOpen}
                />
              )}
            </div>
            <div className="border-t border-neutral-500/10 pt-4 flex items-center justify-between text-neutral-500 dark:text-neutral-400">
              <div className="flex items-center gap-3">
                <span className="text-xs font-medium uppercase tracking-wide">
                  RSS
                </span>
                {RSS_FEEDS.map((feed) => (
                  <a
                    key={feed.href}
                    href={feed.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xl leading-none duration-200 ease-in-out hover:text-primary dark:hover:text-primary"
                    title={feed.label}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {feed.label.split(' ')[0]}
                  </a>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium uppercase tracking-wide">
                  Theme
                </span>
                <DarkModeSwitcher />
              </div>
            </div>
          </div>
        </Dialog.Panel>
      </Dialog>
    </header>
  );
}

// Render document links when the target is server-owned HTML or an external URL. (DL-002)
function renderNavigationItems(
  navigationItems: NavigationItem[],
  setMobileMenuOpen?: Dispatch<SetStateAction<boolean>>,
  currentPathname?: string,
) {
  const isMobile = !!setMobileMenuOpen;

  return navigationItems.map((item) => {
    const isActive = currentPathname === item.to;
    const useDocumentNavigation =
      item.navigationKind === 'document' || /^https?:\/\//.test(item.to);
    const handleClick = setMobileMenuOpen && (() => setMobileMenuOpen(false));

    const menuStyles = cn(
      'duration-300 ease-in-out hover:text-primary dark:hover:text-primary',
      {
        '-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7':
          isMobile,
        'text-sm font-semibold leading-6': !isMobile,
        'text-primary': isActive && isMobile,
        'text-neutral-900 dark:text-white': !isActive || !isMobile,
      },
    );

    // Server-rendered story routes must bypass the SPA router so /stories resolves the HTML document.
    if (useDocumentNavigation) {
      return (
        <a
          href={item.to}
          key={item.name}
          className={menuStyles}
          onClick={handleClick}
        >
          {item.name}
        </a>
      );
    }

    return (
      <ReactRouterLink
        to={item.to}
        key={item.name}
        className={menuStyles}
        onClick={handleClick}
      >
        {item.name}
      </ReactRouterLink>
    );
  });
}
